// marketplace/CategoriesRow.jsx
// Purely presentational — no own fetch, no router Links.
// Receives categories, selectedCategory, and setCategory from ProductGrid.
// Clicking a pill calls setCategory(slug) which updates the shared query state,
// triggering a product refetch exactly like FilterDropdown does.

import React from "react";
import { Package, Tag } from "lucide-react";
import { brand } from "../shared/brand";

export function CategoriesRow({ categories, selectedCategory, setCategory }) {
  // Loading state: show skeleton while ProductGrid is still fetching categories
  if (!categories) {
    return <CategoriesRowSkeleton />;
  }

  // Collapse entirely if no categories came back from the API
  if (categories.length === 0) return null;

  return (
    <div className="mx-auto max-w-7xl px-3 pt-5">
      <div
        className="flex gap-4 overflow-x-auto pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {/* "Todos" pill — clears category filter */}
        <CategoryPill
          label="Todos"
          image={null}
          minPrice={null}
          isActive={selectedCategory === ""}
          isTodos
          onClick={() => setCategory("")}
        />

        {categories.map(cat => (
          <CategoryPill
            key={cat.id}
            label={cat.name}
            image={cat.product_image}
            minPrice={cat.min_price}
            isActive={selectedCategory === cat.slug}
            onClick={() => setCategory(cat.slug)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Category pill button ──────────────────────────────────────────────────────

function CategoryPill({ label, image, minPrice, isActive, isTodos, onClick }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 flex flex-col items-center gap-1.5 group"
    >
      <div
        className="relative size-16 rounded-full overflow-hidden border-2 transition-all duration-200"
        style={{
          borderColor: isActive ? brand.primary : "#e2e8f0",
        }}
      >
        {image ? (
          <img
            src={image}
            alt={label}
            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div
            className="h-full w-full flex items-center justify-center"
            style={{ background: brand.primary + "12" }}
          >
            {isTodos
              ? <Package className="size-7" style={{ color: brand.primary }} />
              : <Tag     className="size-6" style={{ color: brand.primary }} />
            }
          </div>
        )}

        {/* Min price badge */}
        {minPrice != null && !isTodos && (
          <div
            className="absolute -top-1 -right-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white shadow"
            style={{ background: brand.accentGold }}
          >
            S/{Math.round(minPrice)}
          </div>
        )}
      </div>

      <span
        className="text-[11px] font-semibold text-center w-16 leading-tight truncate transition-colors"
        style={{ color: isActive ? brand.primary : "#374151" }}
      >
        {label}
      </span>
    </button>
  );
}

// ── Skeleton shown while categories are loading ───────────────────────────────

function CategoriesRowSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-3 pt-5">
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="shrink-0 flex flex-col items-center gap-2">
            <div className="size-16 rounded-full bg-slate-100 animate-pulse" />
            <div className="h-2.5 w-12 rounded bg-slate-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}